import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { API } from "../../../API";
import { HRAnswerResult, HRQuestionGroup, HRQuestionTemplateId } from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { assertUndefinedable } from "../../../utils/assertUndefinedable";
import {
  getQuestionFieldName,
  getQuestionWithAnswer,
  mapQuestionAnswer,
  shouldSkipQuestionSubmittion,
} from "../../Onboarding/utils/onboardingUtils";

interface Props {
  questionGroups: HRQuestionGroup[];
  currentStage: HRQuestionGroup;
  isStageLoading: boolean;
  answerMutationFn: API["setHRApplicationAnswer"];
  isStageReadOnly: boolean;
  emailVerificationTemplateId: HRQuestionTemplateId | null;
  onCompleted: (group: HRQuestionGroup) => void;
  onInvalidSubmit: (params: { title: string; message: string }) => void;
}

export interface OnboardingForm {
  [x: string]:
    | string
    | number
    | number[]
    | string[]
    | {
        signature: string | null;
        generalSignaturePerson: string | null;
      }
    | {
        startTime: number;
        endTime: number;
      }
    | {
        document: string;
        expiryDate: string | null;
      }
    | null;
}

const useOnboardingForm = (props: Props) => {
  const mutation = useMutation(props.answerMutationFn);
  const initialValues = Object.fromEntries(
    props.questionGroups.flatMap((questionGroup) =>
      questionGroup.questions.flatMap((question) => {
        const answer = mapQuestionAnswer(question);
        return answer === null ? [] : [[getQuestionFieldName(question), answer]];
      })
    )
  );

  const {
    register,
    setValue,
    getValues,
    setFocus,
    watch,
    control,
    handleSubmit: $handleSubmit,
  } = useForm({ defaultValues: initialValues });

  const handleInvalidSubmit = () => {
    props.onInvalidSubmit({
      title: translate(STRINGS.INVALID_FORM),
      message: translate(STRINGS.PLEASE_FILL_REQUIRED_FIELDS),
    });
  };

  const getStageWithFormAnswers = (data: OnboardingForm, stage: HRQuestionGroup) => {
    const questions = stage.questions.map((question) => {
      const fieldName = getQuestionFieldName(question);
      const answer = assertUndefinedable(data[fieldName]);

      if (answer === undefined) {
        return question;
      }

      return getQuestionWithAnswer(question, answer);
    });

    return { ...stage, questions };
  };

  const handleValidSubmit = (data: OnboardingForm) => {
    const stageWithAnswers = getStageWithFormAnswers(data, props.currentStage);

    Promise.all(
      stageWithAnswers.questions.flatMap((question) =>
        shouldSkipQuestionSubmittion(
          data,
          initialValues,
          question,
          props.isStageReadOnly,
          props.emailVerificationTemplateId
        )
          ? []
          : [mutation.mutateAsync(question)]
      )
    )
      .then((results) => {
        if (results.some((result) => !result.isSuccessful)) {
          return props.onInvalidSubmit({
            title: translate(STRINGS.ERROR),
            message: getErrorMessageFromHRAnswerResults(results),
          });
        }

        props.onCompleted(stageWithAnswers);
      })
      .catch((e) => {
        console.error(e);
        props.onInvalidSubmit({
          title: translate(STRINGS.ERROR),
          message: translate(STRINGS.SOMETHING_WENT_WRONG),
        });
      });
  };

  const handleSubmit = $handleSubmit(handleValidSubmit, handleInvalidSubmit);

  return {
    state: {
      isLoading: mutation.isLoading || props.isStageLoading,
      control: control,
    },
    actions: {
      register,
      setValue,
      getValues,
      setFocus,
      watch,
      handleSubmit,
    },
  };
};

function getErrorMessageFromHRAnswerResults(results: HRAnswerResult[]) {
  const message = results.find((result) => !result.isSuccessful)?.reason;

  return message ?? translate(STRINGS.SOMETHING_WENT_WRONG);
}

export default useOnboardingForm;
